/* ============================================================
   Peptide reconstitution & dosing math
   ------------------------------------------------------------
   Vial contains `vialMg` milligrams of lyophilized peptide.
   You add `waterMl` of bacteriostatic water to reconstitute it.
   -> concentration = vialMg / waterMl           (mg per mL)

   For a target dose, convert everything to a common unit (mcg):
     doseMcg = dose in micrograms
   -> volume to inject (mL) = doseMcg / (concentration * 1000)
   -> insulin-syringe units = volumeMl * unitsPerMl
        (U-100 syringe: 100 units = 1 mL; U-40: 40 units = 1 mL)
   -> mcg per single unit  = concentrationMcgPerMl / unitsPerMl
   -> doses per vial       = (vialMg * 1000) / doseMcg
   ============================================================ */

// Insulin-syringe barrel sizes (all U-100: 100 units = 1 mL). `capacity` is the
// max units the barrel holds and drives the graphic's scale + overfill warning.
export const SYRINGES = [
  { id: 's30', capacity: 30, ml: '0.3 mL', label: '0.3 mL (30 units)', unitsPerMl: 100 },
  { id: 's50', capacity: 50, ml: '0.5 mL', label: '0.5 mL (50 units)', unitsPerMl: 100 },
  { id: 's100', capacity: 100, ml: '1 mL', label: '1 mL (100 units)', unitsPerMl: 100 },
]

export const DEFAULT_SYRINGE = 's100'

// Resolve a syringe id, tolerating legacy ids (u100/u40) from older saved calcs.
export function resolveSyringe(id) {
  const legacy = { u100: 's100', u40: 's100' }
  return (
    SYRINGES.find((s) => s.id === (legacy[id] || id)) ||
    SYRINGES.find((s) => s.capacity === 100) ||
    SYRINGES[0]
  )
}

export const DOSE_UNITS = [
  { id: 'mcg', label: 'mcg', toMcg: 1 },
  { id: 'mg', label: 'mg', toMcg: 1000 },
]

const num = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : NaN
}

const round = (n, d = 2) => {
  if (!Number.isFinite(n)) return n
  const f = 10 ** d
  return Math.round(n * f) / f
}

/**
 * Compute reconstitution + dosing results.
 * @returns { ok, errors, ...results }
 */
export function calculate({ vialMg, waterMl, dose, doseUnit = 'mcg', syringe = DEFAULT_SYRINGE }) {
  const errors = {}
  const mg = num(vialMg)
  const water = num(waterMl)
  const doseVal = num(dose)

  if (!(mg > 0)) errors.vialMg = 'Enter the peptide amount in the vial (mg).'
  if (!(water > 0)) errors.waterMl = 'Enter the bacteriostatic water amount (mL).'
  if (!(doseVal > 0)) errors.dose = 'Enter your desired dose.'

  if (Object.keys(errors).length) return { ok: false, errors }

  const unitDef = DOSE_UNITS.find((u) => u.id === doseUnit) || DOSE_UNITS[0]
  const syrDef = resolveSyringe(syringe)
  const unitsPerMl = syrDef.unitsPerMl
  const capacity = syrDef.capacity

  const doseMcg = doseVal * unitDef.toMcg
  const concMgMl = mg / water // mg per mL
  const concMcgMl = concMgMl * 1000 // mcg per mL

  const volumeMl = doseMcg / concMcgMl // mL to inject
  const units = volumeMl * unitsPerMl // insulin-syringe units
  const mcgPerUnit = concMcgMl / unitsPerMl
  const dosesPerVial = (mg * 1000) / doseMcg

  const warnings = []
  if (units > capacity) {
    warnings.push(
      `The dose needs ${round(units, 1)} units — more than a full ${syrDef.label} syringe (${capacity} u). Use a larger syringe, add less water, or split the injection.`
    )
  }
  if (units > 0 && units < 2) {
    warnings.push(
      `Only ${round(units, 2)} units per dose — hard to measure accurately. Consider adding more water for a larger, easier-to-read volume.`
    )
  }
  // Sanity check: a single dose can't be more than the whole vial holds. Usually
  // this means mg/mcg were mixed up (e.g. "5 mg" typed where "5 mcg" was meant).
  if (doseMcg > mg * 1000) {
    warnings.push(
      `That dose (${round(doseMcg, 0).toLocaleString()} mcg) is more than the entire ${mg} mg vial holds. Double-check your dose — did you mean mcg instead of mg?`
    )
  }
  // Measurement resolution: does the draw land on a printed line? U-100 barrels of
  // 30/50 u are marked every 1 unit; the 100 u barrel every 2 units. A value that
  // falls between marks has to be eyeballed, so flag it (skip the tiny/overfill
  // cases already warned above).
  const markStep = capacity <= 50 ? 1 : 2
  const nearestMark = Math.round(units / markStep) * markStep
  if (units >= 2 && units <= capacity && Math.abs(units - nearestMark) > 0.15) {
    warnings.push(
      `${round(units, 2)} units sits between the printed ${markStep}-unit marks on a ${capacity}-unit barrel — you'll have to eyeball it. Round to ${nearestMark} u, or use "Help me pick water" to land on a whole mark.`
    )
  }

  return {
    ok: true,
    errors: {},
    // core outputs
    concentrationMgMl: round(concMgMl, 3),
    concentrationMcgMl: round(concMcgMl, 1),
    volumeMl: round(volumeMl, 4),
    units: round(units, 2),
    mcgPerUnit: round(mcgPerUnit, 2),
    dosesPerVial: Math.floor(dosesPerVial),
    dosesPerVialExact: round(dosesPerVial, 1),
    // echo
    doseMcg,
    unitsPerMl,
    capacity,
    syringeLabel: syrDef.label,
    warnings,
  }
}

/**
 * Reverse calculation: how much bacteriostatic water to add so that each dose
 * comes out to `targetUnits` on the chosen syringe. Returns mL (or null).
 */
export function waterForUnits({ vialMg, dose, doseUnit = 'mcg', targetUnits, syringe = DEFAULT_SYRINGE }) {
  const mg = num(vialMg)
  const d = num(dose)
  const tu = num(targetUnits)
  if (!(mg > 0) || !(d > 0) || !(tu > 0)) return null
  const unitDef = DOSE_UNITS.find((u) => u.id === doseUnit) || DOSE_UNITS[0]
  const doseMcg = d * unitDef.toMcg
  const unitsPerMl = resolveSyringe(syringe).unitsPerMl
  const volumeMl = tu / unitsPerMl // mL per dose to hit targetUnits
  const water = (mg * 1000 * volumeMl) / doseMcg // mL of water to add
  return round(water, 2)
}

/** Human-friendly dose label, e.g. "250 mcg" or "2.5 mg". */
export function formatDose(dose, unit) {
  const n = num(dose)
  if (!Number.isFinite(n)) return '—'
  return `${round(n, 3)} ${unit}`
}
